import React from 'react';
import { getApolloContext } from '@apollo/client/react/context';
import { isExpiredSignedMediaUrl } from '../util/url-meta-media';

// Public/read-only previews try supplied URLs without issuing private refresh queries.
export default function useImageRecovery({ sources = [], refresh, allowRefresh = false, client: suppliedClient }) {
    const { client: contextClient } = React.useContext(getApolloContext());
    const client = suppliedClient || contextClient;
    const urls = [...new Set(sources.filter(source => typeof source === 'string' && source.trim()))];
    const key = JSON.stringify([urls, refresh?.variables, refresh?.field, allowRefresh]);
    const [state, setState] = React.useState({ key, index: 0 });
    const attempted = React.useRef(false);
    const generation = React.useRef(0);
    React.useEffect(() => {
        attempted.current = false;
        generation.current += 1;
        setState({ key, index: 0 });
        return () => { generation.current += 1; };
    }, [key]);
    const current = state.key === key ? state : { index: 0 };
    const src = current.url || urls[current.index];
    const onError = async () => {
        const index = urls.findIndex((url, index) => index > current.index && !isExpiredSignedMediaUrl(url));
        if (!current.url && index !== -1) {
            setState({ key, index });
            return;
        }
        setState({ key, index: urls.length });
        if (!allowRefresh || !refresh || !client || attempted.current) return;
        attempted.current = true;
        const requestGeneration = generation.current;
        try {
            const result = await client.query({ query: refresh.query, variables: refresh.variables, fetchPolicy: 'network-only' });
            const url = (Array.isArray(refresh.field) ? refresh.field : [refresh.field])
                .reduce((value, field) => value?.[field], result.data);
            if (url && generation.current === requestGeneration) setState({ key, index: urls.length, url });
        } catch { /* Exhausted recovery keeps the unavailable placeholder visible. */ }
    };
    return { src, onError };
}
